import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { FiSave, FiArrowLeft, FiUpload } from "react-icons/fi";
import { SavingOverlay } from "../components/SavingOverlay";
import { ErrorMessage } from "../components/ErrorMessage";
import { DeviceInfoDisplay } from "../components/deviceInfo";
import { useRecordSaver } from "../hooks/useRecordSaver";
import { useImageProcessor } from "../hooks/useImageProcessor";
import { useFileUpload } from "../hooks/useFileUpload";
import { darken } from "polished";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.verae[500]};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 24px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ImagePreviewContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background-color: ${({ theme }) => darken(0.04, theme.colors.background)};

  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.verae[50]};
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const IconContainer = styled.div`
  font-size: 2.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const UploadText = styled.span`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const UploadSubtext = styled.span`
  font-size: 0.875rem;
  opacity: 0.7;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 160px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
`;

export const ManualRecordEntry: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);

  const { resizeImageForStorage, getBase64ImageSizeKB } = useImageProcessor({
    onError: setError,
  });
  const [deviceInfo, setDeviceInfo] = useState<Record<string, any>>({
    title: "",
  });

  const { isSaving, hashProgress, throughput, saveRecord } = useRecordSaver({
    onError: setError,
    onSuccess: resetForm,
    resizeImageForStorage,
    getBase64ImageSizeKB,
  });

  const { fileInputRef, handleFileChange, isConverting } = useFileUpload({
    onImageCaptured: setImageDataUrl,
    onResetCamera: () => {},
    onAnalyzeImage: () => {},
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  function resetForm() {
    setImageDataUrl(null);
    setDeviceInfo({ title: "" });
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }
  function handleUploadClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function handleSaveRecord() {
    if (!deviceInfo.title) {
      setError("Title is required before saving");
      return;
    }

    if (imageDataUrl) {
      saveRecord(imageDataUrl, deviceInfo);
    } else {
      // If no image, create a placeholder image with device info text
      const canvas = document.createElement("canvas");
      // High resolution for clarity
      canvas.width = 1600;
      canvas.height = 1200;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Fill background with white
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Create a colored header bar
        ctx.fillStyle = "#03a9f4"; // Primary blue color
        ctx.fillRect(0, 0, canvas.width, 140);
        
        // Add title to the header
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 64px Arial";
        const titleText = deviceInfo.title || "Untitled Record";
        ctx.fillText(titleText, 40, 90);
        
        // Collect fields to display
        const fieldsToShow = [];
        
        // Add fields in order of importance
        if (deviceInfo.description) fieldsToShow.push({ label: "Description", value: deviceInfo.description });
        if (deviceInfo.make) fieldsToShow.push({ label: "Make", value: deviceInfo.make });
        if (deviceInfo.model) fieldsToShow.push({ label: "Model", value: deviceInfo.model });
        if (deviceInfo.serialNumber) fieldsToShow.push({ label: "Serial Number", value: deviceInfo.serialNumber });
        if (deviceInfo.type) fieldsToShow.push({ label: "Type", value: deviceInfo.type });
        
        // Add other fields
        if (deviceInfo.year) fieldsToShow.push({ label: "Year", value: deviceInfo.year });
        if (deviceInfo.processor) fieldsToShow.push({ label: "Processor", value: deviceInfo.processor });
        if (deviceInfo.ram) fieldsToShow.push({ label: "RAM", value: deviceInfo.ram });
        if (deviceInfo.storage) fieldsToShow.push({ label: "Storage", value: deviceInfo.storage });
        if (deviceInfo.osName) fieldsToShow.push({ label: "OS", value: deviceInfo.osName });
        if (deviceInfo.osVersion) fieldsToShow.push({ label: "OS Version", value: deviceInfo.osVersion });
        
        // Add custom fields
        if (deviceInfo.otherDetails) {
          Object.entries(deviceInfo.otherDetails).forEach(([key, value]) => {
            if (value && typeof value === "string") {
              fieldsToShow.push({ label: key, value });
            }
          });
        }
        
        // Limit fields and draw them
        const maxFields = 10;
        const limitedFields = fieldsToShow.slice(0, maxFields);
        
        // Start position for fields
        let y = 200;
        
        // Draw each field with colored label and value
        limitedFields.forEach(({ label, value }) => {
          // Draw label with primary color
          ctx.font = "bold 36px Arial";
          ctx.fillStyle = "#03a9f4";
          ctx.fillText(`${label}:`, 40, y);
          
          // Draw value with dark text color
          ctx.font = "36px Arial";
          ctx.fillStyle = "#1e293b";
          ctx.fillText(`${value}`, 40 + ctx.measureText(`${label}: `).width, y);
          
          y += 80; // Increase spacing between fields
        });
        
        // Add subtle watermark
        ctx.font = "italic 24px Arial";
        ctx.fillStyle = "#64748b";
        ctx.fillText("Generated by Verae", canvas.width - 250, canvas.height - 30);
        
        // Convert to image and save
        const placeholderImage = canvas.toDataURL("image/png");
        saveRecord(placeholderImage, deviceInfo);
      }
    }
  }
  // Handle device info updates
  function handleUpdateDeviceInfo(updatedInfo: Record<string, any>) {
    setDeviceInfo(updatedInfo);
  }

  return (
    <Container>
      <BackButton onClick={handleGoBack}>
        <FiArrowLeft size={18} />
        Back
      </BackButton>

      <Card>
        {error && <ErrorMessage message={error} onDismiss={() => setError("")} />}

        <DeviceInfoDisplay
          deviceInfo={deviceInfo}
          isAIEnhanced={false}
          onReset={resetForm}
          onUpdate={handleUpdateDeviceInfo}
          defaultFields={{
            description: "",
            serialNumber: "",
            make: "",
            model: "",
            type: "",
          }}
        />
      </Card>

      <Card>
        <CardTitle>Image</CardTitle>
        <ImagePreviewContainer onClick={handleUploadClick}>
          {imageDataUrl ? (
            <ImagePreview src={imageDataUrl} alt="Device" />
          ) : (
            <UploadPlaceholder>
              <IconContainer>
                <FiUpload />
              </IconContainer>
              <UploadText>Upload Image</UploadText>
              <UploadSubtext>Click to browse or drop an image file here</UploadSubtext>
            </UploadPlaceholder>
          )}
        </ImagePreviewContainer>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept="image/*,.heic,.heif"
        />
      </Card>

      <Card>
        <ButtonContainer>
          <Button
            onClick={handleSaveRecord}
            variant="primary"
            size="xl"
            icon={<FiSave />}
            disabled={isSaving || !deviceInfo.title}
          >
            Save Record
          </Button>
          <Button onClick={resetForm} variant="outline" size="xl" disabled={isSaving}>
            Reset
          </Button>
        </ButtonContainer>
      </Card>

      {isSaving && <SavingOverlay isVisible={isSaving} progress={hashProgress} throughput={throughput} />}

      {isConverting && (
        <SavingOverlay isVisible={isConverting} throughput={0} progress={0} message="Converting image format..." />
      )}
    </Container>
  );
};
